import React, { useState } from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { IAPIResponse, IPhoto, IPhotoLocation } from "../../types/api";
import {
    getPhotoLocations,
    getPhotos,
    rejectPhoto,
    updatePhoto,
} from "../../lib/api";
import { defaultDateFormat } from "../../lib/utils";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { alertActions } from "../../features/alert/alertSlice";
import Select from "react-select";
import { useLoaderData } from "react-router";
import {
    globalActions,
    selectAgeLevels,
} from "../../features/global/globalSlice";
import { store } from "../..";
import { UPLOADED_IMAGE_URL } from "../../lib/globals";
import ImageModal from "../../components/ImageModal";
import { IImage } from "../../types/shared";

interface Filters {
    team?: string;
    ageLevels: {
        options?: Array<string>;
        filter?: Array<string>;
    };
    email?: string;
    pendingApproval?: number;
}

export const loader = async () => {
    const ageLevels = store.getState().global.ageLevels;

    const ageLevelsPromise = ageLevels.length
        ? Promise.resolve()
        : store.dispatch(globalActions.getAgeLevels());

    const [_, photos, locations] = await Promise.all([
        ageLevelsPromise,
        getPhotos(),
        getPhotoLocations(),
    ]);

    return {
        photos,
        locations,
    };
};

const PhotoApproval = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const ageLevels = useAppSelector(selectAgeLevels);
    const {
        photos: { photos: loadedPhotos },
        locations: { photoLocations: locations },
    } = useLoaderData() as {
        photos: IAPIResponse & {
            photos: IPhoto[];
        };
        locations: IAPIResponse & {
            photoLocations: IPhotoLocation[];
        };
    };
    const [photos, setPhotos] = useState<IPhoto[]>(loadedPhotos);
    const [modalShow, setModalShow] = useState<boolean>(false);
    const [modalContent, setModalContent] = useState<IImage>({
        image: "",
        alt: "",
    });
    const [photoLocations, setPhotoLocations] = useState<
        Record<number, number | null>
    >(
        photos.reduce(
            (acc, p) => {
                p.meta.location && (acc[p.id] = p.meta.location.id);
                return acc;
            },
            {} as Record<number, number | null>,
        ),
    );

    const [filters, setFilters] = useState<Filters>({
        team: undefined,
        ageLevels: {
            options: Array.from(new Set(ageLevels.map(a => a.ageLevel))),
            filter: undefined,
        },
        email: undefined,
        pendingApproval: undefined,
    });

    const user = useAppSelector(state => state.auth.user);

    const handleApproval = async (photoId: IPhoto["id"]) => {
        await updatePhoto(photoId, photoLocations[photoId], user!.id)
            .then(response => {
                setPhotos(prev =>
                    prev?.map(p =>
                        p.id === response.photo.id ? response.photo : p,
                    ),
                );
            })
            .catch(e => {
                dispatch(
                    alertActions.error({
                        title: "There was a problem approving the photo",
                        text: "If the problem persists, please contact a site admin.",
                        dismissible: true,
                    }),
                );
            });
    };

    const handleReject = async (photoId: IPhoto["id"]) => {
        await rejectPhoto(photoId, user!.id)
            .then(response => {
                setPhotos(prev => prev.filter(p => p.id !== photoId));
            })
            .catch(e => {
                dispatch(
                    alertActions.error({
                        title: "There was a problem rejecting the photo",
                        text: "If the problem persists, please contact a site admin.",
                        dismissible: true,
                    }),
                );
            });
    };

    return (
        <section
            className="photoApprovalContent"
            data-testid="photoApprovalContent"
        >
            <Row>
                <Col xs={12} md={6} xl={3} className="py-2 py-xl-0">
                    <h5>Age Level:</h5>
                    <Select
                        isSearchable={false}
                        isClearable={true}
                        isMulti={true}
                        options={filters.ageLevels.options?.map((a, idx) => {
                            return {
                                value: idx,
                                label: a,
                            };
                        })}
                        onChange={a => {
                            setFilters(prev => ({
                                ...prev,
                                ageLevels: {
                                    ...prev.ageLevels,
                                    filter:
                                        a.length === 0
                                            ? undefined
                                            : a?.map(o => o.label),
                                },
                            }));
                        }}
                    />
                </Col>
                <Col xs={12} md={6} xl={3} className="py-2 py-xl-0">
                    <h5>Team:</h5>
                    <Form.Control
                        type="text"
                        placeholder="Team"
                        onChange={e => {
                            setFilters(prev => ({
                                ...prev,
                                team:
                                    e.target.value.trim() === ""
                                        ? undefined
                                        : e.target.value.trim(),
                            }));
                        }}
                    />
                </Col>
                <Col xs={12} md={6} xl={3} className="py-2 py-xl-0">
                    <h5>Email:</h5>
                    <Form.Control
                        type="text"
                        placeholder="Email"
                        onChange={e => {
                            setFilters(prev => ({
                                ...prev,
                                email:
                                    e.target.value.trim() === ""
                                        ? undefined
                                        : e.target.value.trim(),
                            }));
                        }}
                    />
                </Col>
                <Col xs={12} md={6} xl={3} className="py-2 py-xl-0">
                    <h5>Pending Approval Only</h5>
                    <Select
                        isSearchable={false}
                        isClearable={true}
                        options={[
                            {
                                value: 0,
                                label: "No",
                            },
                            {
                                value: 1,
                                label: "Yes",
                            },
                        ]}
                        defaultValue={{
                            value: 0,
                            label: "No",
                        }}
                        onChange={u => {
                            setFilters(prev => ({
                                ...prev,
                                pendingApproval: u ? u.value : undefined,
                            }));
                        }}
                    />
                </Col>
            </Row>

            <Table className="photosTable" striped bordered responsive>
                <thead>
                    <tr>
                        <th>Photo</th>
                        <th>Photo Upload Date</th>
                        <th>Photo Info</th>
                        <th>
                            Approved /<br />
                            Updated
                        </th>
                        <th>Photo Location</th>
                        <th>
                            Approve /<br />
                            Reject
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {photos
                        ?.filter(p =>
                            filters.ageLevels.filter
                                ? p.meta.ageLevel?.ageLevel
                                    ? filters.ageLevels.filter.includes(
                                          p.meta.ageLevel?.ageLevel,
                                      )
                                    : true
                                : true,
                        )
                        .filter(p =>
                            filters.email
                                ? p.meta.email
                                    ? p.meta.email
                                          .toLowerCase()
                                          .includes(filters.email.toLowerCase())
                                    : true
                                : true,
                        )
                        .filter(p =>
                            filters.team
                                ? p.meta.teamName
                                    ? p.meta.teamName
                                          .toLowerCase()
                                          .includes(filters.team.toLowerCase())
                                    : true
                                : true,
                        )
                        .filter(p =>
                            filters.pendingApproval === 1
                                ? p.approvalDate === null
                                : true,
                        )
                        .map((p, idx) => (
                            <tr key={idx}>
                                <td width={"12%"}>
                                    <img
                                        src={`${UPLOADED_IMAGE_URL}${p.photo}`}
                                        onClick={() => {
                                            setModalContent({
                                                image: p.photo,
                                                alt: p.meta.caption || "",
                                            });
                                            setModalShow(true);
                                        }}
                                    />
                                </td>
                                <td width={"12%"}>
                                    {p.uploadDate &&
                                        defaultDateFormat.format(
                                            new Date(p.uploadDate),
                                        )}
                                </td>
                                <td width={"15%"}>
                                    <b>Team:</b> {p.meta.teamName}
                                    <br />
                                    <b>Age Level:</b>{" "}
                                    {p.meta.ageLevel?.ageLevel}
                                    <br />
                                    <b>Uploaded By:</b> {p.meta.firstName}{" "}
                                    {p.meta.lastName}
                                    <br />
                                    <b>Email:</b> {p.meta.email}
                                </td>
                                <td width={"13%"}>
                                    {p.approvalDate && (
                                        <>
                                            {defaultDateFormat.format(
                                                new Date(p.approvalDate),
                                            )}
                                            <br />
                                            {p.approvedBy.name}
                                        </>
                                    )}
                                </td>
                                <td width={"28%"}>
                                    <Select
                                        isSearchable={false}
                                        isClearable={true}
                                        isMulti={false}
                                        options={locations.map(l => {
                                            return {
                                                value: l.id,
                                                label: l.name,
                                            };
                                        })}
                                        defaultValue={
                                            photoLocations[p.id]
                                                ? {
                                                      value: photoLocations[
                                                          p.id
                                                      ],
                                                      label: p.meta.location
                                                          ?.name,
                                                  }
                                                : null
                                        }
                                        onChange={l => {
                                            setPhotoLocations(prev => ({
                                                ...prev,
                                                [p.id]: l?.value || null,
                                            }));
                                        }}
                                    />
                                </td>
                                <td width={"20%"}>
                                    <Button
                                        variant="primary"
                                        onClick={() => handleApproval(p.id)}
                                    >
                                        {p.approvalDate === null
                                            ? "Approve"
                                            : "Update"}
                                    </Button>
                                    <Button
                                        variant="danger"
                                        onClick={() => handleReject(p.id)}
                                    >
                                        {p.approvalDate === null
                                            ? "Reject"
                                            : "Delete"}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                </tbody>
            </Table>
            <ImageModal
                show={modalShow}
                image={modalContent.image}
                alt={modalContent.alt}
                onHide={() => setModalShow(false)}
            />
        </section>
    );
};

export default PhotoApproval;
